import { Table } from 'antd';
import { useDispatch } from 'react-redux';
import { getImageUrl } from '../../../../utils/image-helper/get-image-url';
import { Link } from 'react-router-dom';
import { showLoadingAlertMessage } from '../../../../utils/show-loading-alert-message/show-loading-alert-message';
import { networkCall } from '../../../../utils/network-call/network-call';
import { showDefaultErrorMessages } from '../../../../utils/show-default-error-message/show-default-error-messages';
import { AlertMessageState, clearAlertMessage, showAlertMessage } from '../../../../redux/reducer/alert-message.reducer';
import { AlertMessages } from '../../../../enums/alert-messages.enum';
import { CMSButton } from '../../../../components/cms/button/cms-button.';
import { formatTimestampToDate } from '../../../../utils/date/format-timestamp-to-date';
import { License } from '../../types/api/create-license-success-response';

type DeleteColumnProps = {
    license: License;
    onDelete: (id: number) => void;
};

const DeleteColumn = ({ license: { id }, onDelete }: DeleteColumnProps) => {
    const dispatch = useDispatch();

    const handleClick: React.MouseEventHandler<HTMLAnchorElement> = async (e) => {
        e.preventDefault();
        e.stopPropagation();

        if(window.confirm('Biztosan törlöd a licenct?')) {
            showLoadingAlertMessage();

            const response = await networkCall(`/licenses/${ id }`, {
                method: 'DELETE'
            });;

            const messageShowed = showDefaultErrorMessages(response);
            if(messageShowed) return;

            if(response?.status === 204) {
                dispatch(showAlertMessage({
                    alertMessageType: 'success',
                    alertMessageText: 'Törlés sikeres.'
                }));
                onDelete(id);
                return;
            }

            dispatch(clearAlertMessage());
            const alertMessageState: AlertMessageState = {
                alertMessageType: 'error',
                alertMessageText: AlertMessages.UnknownErrorTryAgain
            };

            if(response?.status === 404) {
                alertMessageState.alertMessageText = 'Törlés sikertelen, a törlendő licenc nem létezik.';
            }

            dispatch(showAlertMessage(alertMessageState));
        }
    }

    return (
        <a onClick={handleClick}>
            <img src={getImageUrl('delete-icon2.png')} alt="delete-icon2" />
        </a>
    );
}

type LicenseListProps = {
    companyId: number;
    licenses: License[];
    onDelete: (id: number) => void;
};

export const LicenseList = ({ companyId, licenses, onDelete }: LicenseListProps) => {
    const columns = [
        {
            title: 'ID',
            dataIndex: 'id',
            sorter: (a: License, b: License) => a.id - b.id
        },
        {
            title: 'Licenc',
            render: (license: License) => {
                if (!license.finalized || !license.startDate || !license.endDate) return <b>nem érvényes</b>; 
                
                const startDate = new Date(license.startDate*1000);
                const endDate = new Date(license.endDate*1000);
                const today = new Date();

                startDate.setHours(0, 0, 0, 0);
                endDate.setHours(0, 0, 0, 0);
                today.setHours(0, 0, 0, 0);

                if(startDate.getTime() <= today.getTime() && endDate.getTime() >= today.getTime())
                    return <><b>érvényes</b> ({formatTimestampToDate(license.startDate)} - {formatTimestampToDate(license.endDate)})</>;
                else return <b>nem érvényes</b>;
            }
        },
        {
            title: 'Véglegesítve',
            render: (license: License) => {
                return license.finalized === true ? 'igen' : 'nem';
            }
        },
        {
            title: 'Adatlap',
            render: (record: License) => {
                return (
                    <Link to={`/cms/admin/licenses/edit/${ record.id }`}>
                        <img src={getImageUrl('edit-icon.png')} alt="edit-icon" />
                    </Link>
                );
            }
        },
        {
            title: 'Törlés',
            render: (record: License) => {
                return (
                    <DeleteColumn license={record} onDelete={onDelete}/>
                );
            }
        }
    ];

    return (
        <>
            <b>Licencek</b>
            <Table
                className='table-responsive data-table'
                columns={columns}
                dataSource={licenses}
                bordered
                pagination={
                    {
                        position: ['bottomRight'],
                        pageSize: 10
                    }
                }
                footer={
                    () => (
                        <div className='text-end'>
                            <Link to={`/cms/admin/companies/${ companyId }/licenses/add`}>
                                <CMSButton text='+' type='pink' />
                            </Link>
                        </div>
                    )
                }
            />
        </>
    );
};